/**
 * Template Loader Utility
 *
 * Dynamically loads all training template JSON files from the data folder.
 * Supports hot module replacement for instant updates during development.
 * Uses module-level caching for O(1) lookups after first access.
 */

import { getTemplateFolder, type TemplateFolder } from "./templateFolders";

type JsonPrimitive = string | number | boolean | null;
export type JsonValue =
  | JsonPrimitive
  | JsonValue[]
  | { [key: string]: JsonValue };

export interface TemplateMetadata {
  id: string;
  name: string;
  description: string;
  filePath: string;
  fileName: string;
  category: string;
  folder: TemplateFolder;
}

export interface TemplateData {
  name: string;
  description?: string;
  pages: JsonValue[];
  settings?: JsonValue;
  tags?: string[];
  [key: string]: JsonValue | undefined;
}

/**
 * Dynamically imports all JSON template files from the organized templates directory
 * using Vite's glob import feature for automatic discovery and hot reload
 * Loads from hierarchical structure: atoms, molecules, organisms, pages
 */
const templateModules = import.meta.glob<TemplateData>(
  "/src/data/templates/**/*.json",
  {
    eager: true,
    import: "default",
  },
);

// Module-level caches for O(1) repeated access
let _cachedTemplates: TemplateMetadata[] | null = null;
let _templateByIdMap: Map<string, TemplateData> | null = null;

function ensureIdMap(): Map<string, TemplateData> {
  if (_templateByIdMap) return _templateByIdMap;
  _templateByIdMap = new Map();
  for (const [path, data] of Object.entries(templateModules)) {
    const fileName = path.split("/").pop() || "";
    const id = fileName.replace(".json", "");
    _templateByIdMap.set(id, data);
  }
  return _templateByIdMap;
}

/**
 * Get all available templates with metadata
 */
export function getAllTemplates(): TemplateMetadata[] {
  if (_cachedTemplates) return _cachedTemplates;

  const templates: TemplateMetadata[] = [];

  for (const [path, data] of Object.entries(templateModules)) {
    // Extract filename from path (e.g., /src/data/templates/pages/sensor-dashboard.json -> sensor-dashboard.json)
    const fileName = path.split("/").pop() || "";
    const id = fileName.replace(".json", "");
    const category = path.split("/templates/")[1]?.split("/")[0] || "misc";
    const name = data.name || fileName.replace(".json", "").replace(/-/g, " ");
    const description = data.description || "No description available";
    const folder = getTemplateFolder({
      id,
      category,
      name,
      description,
      tags: data.tags,
    });

    // All files in templates/ subdirectories are valid templates
    templates.push({
      id,
      name,
      description,
      filePath: path,
      fileName,
      category,
      folder,
    });
  }

  _cachedTemplates = templates.sort(
    (a, b) =>
      a.folder.order - b.folder.order ||
      a.folder.label.localeCompare(b.folder.label) ||
      a.name.localeCompare(b.name),
  );
  return _cachedTemplates;
}

/**
 * Get a specific template by ID
 * Uses a pre-built Map for O(1) lookups instead of linear scan
 */
export function getTemplateById(id: string): TemplateData | null {
  const map = ensureIdMap();
  const result = map.get(id);
  if (!result) {
    console.error(`Template not found: ${id}`);
    return null;
  }
  return result;
}

/**
 * Get template data by filename
 */
export function getTemplateByFileName(fileName: string): TemplateData | null {
  const id = fileName.replace(".json", "");
  return getTemplateById(id);
}

/**
 * Get first available template (useful for default selection)
 */
export function getFirstTemplate(): {
  metadata: TemplateMetadata;
  data: TemplateData;
} | null {
  const templates = getAllTemplates();

  if (templates.length === 0) {
    return null;
  }

  const firstTemplate = templates[0];
  const data = getTemplateById(firstTemplate.id);

  if (!data) {
    return null;
  }

  return {
    metadata: firstTemplate,
    data,
  };
}

/**
 * Search templates by name or description
 */
export function searchTemplates(query: string): TemplateMetadata[] {
  const allTemplates = getAllTemplates();
  const lowerQuery = query.toLowerCase();

  return allTemplates.filter(
    (template) =>
      template.name.toLowerCase().includes(lowerQuery) ||
      template.description.toLowerCase().includes(lowerQuery) ||
      template.id.toLowerCase().includes(lowerQuery),
  );
}
